import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconClose, IconDownload, IconRetry, IconZoomIn, IconZoomOut } from "./Icons";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;
const VIEWER_LOAD_TIMEOUT_MS = 24_000;
const VIEWER_FALLBACK_DELAY_MS = 600;

const clampZoom = (value: number): number =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));

function retryUrl(src: string, attempt: number): string {
  if (attempt === 0 || src.startsWith("data:") || src.startsWith("blob:")) return src;
  return `${src}${src.includes("?") ? "&" : "?"}viewer_retry=${attempt}`;
}

/**
 * 全站公用图片查看器。
 *
 * 只接收 URL，不知道聊天、消息或画图任务是什么；用户图片与未来生成图都通过
 * `src + downloadUrl` 接入。放大后可拖动画面，滚轮、按钮与键盘 +/- 都能缩放。
 */
export function ImageViewer({
  open,
  src,
  alt,
  ariaLabel = "图片查看器",
  downloadUrl,
  downloadName = "dreamverse-image",
  fallbackSrc,
  onClose,
}: {
  open: boolean;
  src: string;
  alt: string;
  ariaLabel?: string;
  downloadUrl?: string;
  downloadName?: string;
  /** 主地址失败后使用的同源鉴权回源地址。 */
  fallbackSrc?: string;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retryTimerRef = useRef<number | null>(null);
  const drag = useRef<{
    pointerId: number;
    x: number;
    y: number;
    ox: number;
    oy: number;
    moved: boolean;
  } | null>(null);
  const suppressStageClick = useRef(false);
  const displaySrc = useMemo(
    () => retryUrl(attempt > 0 && fallbackSrc ? fallbackSrc : src, attempt),
    [attempt, fallbackSrc, src],
  );

  const changeZoom = useCallback((delta: number): void => {
    setZoom((current) => clampZoom(current + delta));
  }, []);

  const resetView = useCallback((): void => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const retry = (): void => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setFailed(false);
    setLoaded(false);
    setAttempt((value) => value + 1);
  };

  const handleLoadFailure = useCallback((): void => {
    setLoaded(false);
    if (!fallbackSrc || attempt > 0) {
      setFailed(true);
      return;
    }
    if (retryTimerRef.current !== null) return;
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      setAttempt(1);
    }, VIEWER_FALLBACK_DELAY_MS);
  }, [attempt, fallbackSrc]);

  useEffect(() => {
    if (!open) {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      return;
    }
    resetView();
    setFailed(false);
    setLoaded(false);
    setAttempt(0);
    suppressStageClick.current = false;
  }, [open, resetView, src]);

  useEffect(
    () => () => {
      if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!open || loaded || failed) return;
    const timer = window.setTimeout(handleLoadFailure, VIEWER_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [displaySrc, failed, handleLoadFailure, loaded, open]);

  useEffect(() => {
    if (zoom <= 1) setOffset({ x: 0, y: 0 });
  }, [zoom]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
      if (event.key === "+" || event.key === "=") changeZoom(ZOOM_STEP);
      if (event.key === "-") changeZoom(-ZOOM_STEP);
      if (event.key === "0") resetView();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [changeZoom, onClose, open, resetView]);

  if (!open) return null;

  return createPortal(
    <div className="image-viewer-backdrop fade-in" role="presentation" onClick={onClose}>
      <section
        className="image-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="image-viewer__header">
          <div className="image-viewer__header-actions">
            {downloadUrl ? (
              <a
                className="image-viewer__action"
                href={downloadUrl}
                download={downloadName}
                aria-label="下载原图"
                title="下载原图"
              >
                <IconDownload size={19} />
              </a>
            ) : null}
            <button
              type="button"
              className="image-viewer__action"
              onClick={onClose}
              aria-label="关闭图片"
              title="关闭"
              autoFocus
            >
              <IconClose size={20} />
            </button>
          </div>
        </header>

        <div
          className={`image-viewer__stage${zoom > 1 && !failed ? " can-drag" : ""}${dragging ? " is-dragging" : ""}`}
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            if (suppressStageClick.current) {
              suppressStageClick.current = false;
              return;
            }
            onClose();
          }}
          onWheel={(event) => {
            event.preventDefault();
            changeZoom(event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
          }}
          onDoubleClick={() => (zoom === 1 ? changeZoom(1) : resetView())}
          onPointerDown={(event) => {
            if (zoom <= 1 || failed) return;
            suppressStageClick.current = false;
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = {
              pointerId: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              ox: offset.x,
              oy: offset.y,
              moved: false,
            };
            setDragging(true);
          }}
          onPointerMove={(event) => {
            const start = drag.current;
            if (!start || start.pointerId !== event.pointerId) return;
            if (Math.abs(event.clientX - start.x) + Math.abs(event.clientY - start.y) > 4) {
              start.moved = true;
            }
            setOffset({
              x: start.ox + event.clientX - start.x,
              y: start.oy + event.clientY - start.y,
            });
          }}
          onPointerUp={(event) => {
            if (drag.current?.pointerId !== event.pointerId) return;
            suppressStageClick.current = drag.current.moved;
            drag.current = null;
            setDragging(false);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => {
            drag.current = null;
            suppressStageClick.current = false;
            setDragging(false);
          }}
        >
          {failed ? (
            <button type="button" className="image-viewer__retry" onClick={retry}>
              <IconRetry size={24} />
              <strong>图片没有加载出来</strong>
              <span>点这里重新加载</span>
            </button>
          ) : (
            <>
              <img
                key={displaySrc}
                className="image-viewer__image"
                src={displaySrc}
                alt={alt}
                draggable={false}
                onClick={(event) => event.stopPropagation()}
                onLoad={() => {
                  if (retryTimerRef.current !== null) {
                    window.clearTimeout(retryTimerRef.current);
                    retryTimerRef.current = null;
                  }
                  setLoaded(true);
                }}
                onError={handleLoadFailure}
                style={{ transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${zoom})` }}
              />
              {!loaded ? (
                <span className="image-viewer__loading" role="status">
                  <IconRetry size={22} />
                  图片加载中
                </span>
              ) : null}
            </>
          )}
        </div>

        <footer className="image-viewer__controls" aria-label="图片缩放">
          <button
            type="button"
            className="image-viewer__control"
            onClick={() => changeZoom(-ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            aria-label="缩小"
            title="缩小（-）"
          >
            <IconZoomOut size={20} />
          </button>
          <button
            type="button"
            className="image-viewer__scale"
            onClick={resetView}
            aria-label="恢复原始缩放"
            title="恢复 100%（0）"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="image-viewer__control"
            onClick={() => changeZoom(ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            aria-label="放大"
            title="放大（+）"
          >
            <IconZoomIn size={20} />
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
